import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import styles from './PageHeader.module.css';

interface PageHeaderProps {
  title: string;
  /** Short line under the title, e.g. today's date. */
  subtitle?: ReactNode;
  /** Buttons on the right of the title row. */
  actions?: ReactNode;
  /** Extra row below the title (toolbars, filters). */
  children?: ReactNode;
  /** Shows a "Wróć" button; the path is used when there is no in-app history (e.g. a shared link). */
  back?: string;
}

/** Installed PWAs on iOS have no browser back button, so detail screens bring their own. */
function BackButton({ fallback }: { fallback: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className={styles.back}
      onClick={() => {
        const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (idx > 0) navigate(-1);
        else navigate(fallback, { replace: true });
      }}
    >
      <ChevronLeft aria-hidden="true" />
      <span>Wróć</span>
    </button>
  );
}

/**
 * Phones: a compact navy app bar — "‹ Wróć", the title and the actions on one row.
 * Larger screens: a plain page heading with "Wróć" above it.
 */
export function PageHeader({ title, subtitle, actions, children, back }: PageHeaderProps) {
  return (
    <header className={back ? `${styles.header} ${styles.withBack}` : styles.header}>
      <div className={styles.bar}>
        {back && <BackButton fallback={back} />}
        <div className={styles.titles}>
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {children && <div className={styles.extra}>{children}</div>}
    </header>
  );
}
